import {
  superAdminLogin,
  regularUserLogin,
  register as registerService,
  employeeAppLogin,
  resetPassword
} from "services/authService";
import {
  loginStart,
  loginSuccess,
  loginFailure,
  registerStart,
  registerSuccess,
  registerFailure,
  logout,
} from "store/features/auth/authSlices";
import {
  setSecureLocalItem,
  removeSecureLocalItem,
  getSecureLocalItem,
} from "utils/secureSessionStorage";
import { showSuccessToast, showErrorToast } from "utils/toastNotification";

// Super Admin Login Action
export const superAdminLoginAction = (credentials) => async (dispatch) => {
  try {
    dispatch(loginStart());

    const result = await superAdminLogin(credentials);

    if (result?.meta?.status === true && result?.meta?.code === 200) {
      const { token, user } = result.data;

      setSecureLocalItem("token", token);
      setSecureLocalItem("role", user.role);
      setSecureLocalItem("user", JSON.stringify(user));

      dispatch(
        loginSuccess({
          token: token,
          user: user,
        })
      );

      showSuccessToast(result.meta.message || "Super Admin Login Successful!");
      return { success: true, role: user.role };
    } else {
      console.error("Unexpected response structure:", result);
      showErrorToast(result?.meta?.message || "Login Failed!");
      throw new Error(result?.meta?.message || "Login Failed!");
    }
  } catch (error) {
    console.error("Login error details:", error);

    if (error?.meta) {
      showErrorToast(error.meta.message || "Login Failed!");
      dispatch(loginFailure(error.meta.message));
    } else {
      showErrorToast("Login Failed! Please check your credentials.");
      dispatch(loginFailure(error.message || "Login Failed"));
    }

    return { success: false };
  }
};

// Regular User Login Action
export const regularUserLoginAction = (credentials) => async (dispatch) => {
  try {
    dispatch(loginStart());

    const result = await regularUserLogin(credentials);

    if (result?.meta?.status === true && result?.meta?.code === 200) {
      const { token, user } = result.data;

      setSecureLocalItem("token", token);
      setSecureLocalItem("role", user.role);
      setSecureLocalItem("user", JSON.stringify(user));

      dispatch(
        loginSuccess({
          token: token,
          user: user,
        })
      );

      showSuccessToast(result.meta.message || "Login Successful!");
      return { success: true, role: user.role };
    } else {
      console.error("Unexpected response structure:", result);
      showErrorToast(result?.meta?.message || "Login Failed!");
      throw new Error(result?.meta?.message || "Login Failed!");
    }
  } catch (error) {
    console.error("Login error details:", error);

    if (error?.meta) {
      showErrorToast(error.meta.message || "Login Failed!");
      dispatch(loginFailure(error.meta.message));
    } else {
      showErrorToast("Login Failed! Please check your credentials.");
      dispatch(loginFailure(error.message || "Login Failed"));
    }

    return { success: false };
  }
};

// Unified Login Action (Auto-detects based on email)
export const login = (credentials) => async (dispatch) => {
  if (credentials.email === "superadmin@gmail.com") {
    return await dispatch(superAdminLoginAction(credentials));
  } else {
    return await dispatch(regularUserLoginAction(credentials));
  }
};

// Employee Login Action
export const employeeLogin = (credentials) => async (dispatch) => {
  try {
    dispatch(loginStart());

    const result = await employeeAppLogin(credentials);

    if (result?.meta?.status === true && result?.meta?.code === 200) {
      const { token, user } = result.data;

      // Since employee login might not return role in user object, ensure it's set
      const userRole = user.role || "Employee";

      setSecureLocalItem("token", token);
      setSecureLocalItem("role", userRole);
      setSecureLocalItem("user", JSON.stringify({ ...user, role: userRole }));

      dispatch(
        loginSuccess({
          token: token,
          user: { ...user, role: userRole },
        })
      );

      showSuccessToast(result.meta.message || "Employee Login Successful!");
      return { success: true, role: userRole };
    } else {
      console.error("Unexpected response structure:", result);
      showErrorToast(result?.meta?.message || "Login Failed!");
      throw new Error(result?.meta?.message || "Login Failed!");
    }
  } catch (error) {
    console.error("Login error details:", error);

    if (error?.meta) {
      showErrorToast(error.meta.message || "Login Failed!");
      dispatch(loginFailure(error.meta.message));
    } else {
      showErrorToast("Login Failed! Please check your credentials.");
      dispatch(loginFailure(error.message || "Login Failed"));
    }

    return { success: false };
  }
};

// Register Action
export const registerUser = (userData) => async (dispatch) => {
  try {
    dispatch(registerStart());
    const result = await registerService(userData);

    // Success case (code 200)
    if (result?.meta?.status === true && result?.meta?.code === 200) {
      dispatch(registerSuccess());
      showSuccessToast(
        result.meta.message || "Registration successful! Please login."
      );
      return { success: true, data: result.data };
    }
    // Subdomain exists case (status false, code 500)
    else if (
      result?.meta?.status === false &&
      (result?.meta?.code === 500 ||
        result?.meta?.message?.toLowerCase().includes("subdomain"))
    ) {
      const errorMsg =
        "This subdomain is already taken. Please choose another one.";
      dispatch(registerFailure(errorMsg));
      // Don't show error toast here - show inline error instead
      return { success: false, error: errorMsg, field: "subdomain" };
    }
    // Other errors
    else {
      const errorMsg = result?.meta?.message || "Registration failed!";
      dispatch(registerFailure(errorMsg));
      showErrorToast(errorMsg);
      return { success: false, error: errorMsg };
    }
  } catch (error) {
    console.error("Registration error:", error);
    const errorMsg =
      error?.response?.data?.meta?.message ||
      error.message ||
      "Registration failed!";

    if (errorMsg?.toLowerCase().includes("subdomain")) {
      return {
        success: false,
        error: "This subdomain is already taken. Please choose another one.",
        field: "subdomain",
      };
    }

    showErrorToast(errorMsg);
    return { success: false, error: errorMsg };
  }
};

export const resetPasswordAction = (passwordData) => async (dispatch) => {
  try {
    dispatch(loginStart());

    const result = await resetPassword(passwordData);

    if (result?.meta?.status === true && result?.meta?.code === 200) {
      dispatch(loginSuccess({
        token: getSecureLocalItem("token"),
        user: JSON.parse(getSecureLocalItem("user")),
      }));

      showSuccessToast(result.meta.message || "Password updated successfully.");

      return {
        success: true,
      };
    }

    throw new Error(result?.meta?.message || "Password reset failed");
  } catch (error) {
    dispatch(loginFailure(error.message));

    showErrorToast(
      error?.meta?.message ||
      error.message ||
      "Password reset failed."
    );

    return {
      success: false,
    };
  }
};

// Logout Action
export const logoutAction = () => async (dispatch) => {
  try {
    removeSecureLocalItem("token");
    removeSecureLocalItem("role");
    removeSecureLocalItem("user");
    removeSecureLocalItem("subscription_features");
    removeSecureLocalItem("subscription_permissions");
    removeSecureLocalItem("subscription");
    dispatch(logout());
  } catch (error) {
    console.error("Logout failed:", error);
  }
};
